/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {getDeviceConfigurations} from '@ion/core/src/services/Connect';
import {GenericObject} from '@ion/core/src/types/entities';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import {Options} from '@ion/dls/src/components/menus/Dropdown';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {
  getOptionLabel,
  getOptions,
  getOptionSelected
} from '@ion/features/src/helpers';

// defines the notification type chooser prop definitions ...
export type DeviceConfigurationChooserProps = {
  accountId: number;
  deviceType?: GenericObject;
  selectedValue?: GenericObject;
  onChange?: (deviceConfiguration: GenericObject) => void;
  readOnly?: boolean;
  required?: boolean;
  hasError?: boolean;
  classes?: string;
  defaultValue?: string;
  helperText?: string;
  isHardwarePart?: boolean;
}

// defines the styles used by notification type chooser ...
const noPadAndMargin = {
  padding : '0px !important',
  margin  : '0px !important'
};
export const useStyles = makeStyles(theme => ({
  root : {
    ...noPadAndMargin
  },
  listbox : {
    ...noPadAndMargin
  },
  popper : {
    zIndex : 4000
  },
  paper : {
    padding : '0px !important'
  },
  option : {
    padding       : '17px !important',
    ...dlsAlt('components', 'dropdown'),
    color         : dlsColor('grey3'),
    textTransform : ({upperCase}: { upperCase: boolean }) => (
      upperCase ? 'uppercase' : 'none'
    ),
    letterSpacing : ({upperCase}: { upperCase: boolean }) => (
      upperCase ? dlsAlt('components', 'dropdown').letterSpacing : 'normal'
    ),
    '&[data-focus="true"]' : {
      backgroundColor : `${dlsAlt('components', 'icon', 'selected')} !important`
    }
  },
  endAdornment : {
    paddingRight : '7px !important'
  },
  input : {
    paddingLeft  : '7px !important',
    paddingRight : '3em !important'
  },
  formControl : {
    borderBottom           : ({hasError}: { hasError: boolean;}) => hasError ? 'none' : `1px solid ${dlsColor('grey5')}`,
    '& .MuiInputBase-root' : {
      width        : '100%',
      marginTop    : 0,
      border       : ({hasError}: { hasError: boolean; }) => hasError ? `solid 2px ${dlsColor('caRed')}` : 'none',
      borderBottom : ({hasError}: { hasError: boolean }) => hasError ? '0px' : 'none'
    }
  },
  inputRoot : {
    '&::before' : {
      borderBottom : 'none !important'
    },
    '&.Mui-focused' : {
      '&::before' : {
        borderBottom : `1px solid ${dlsColor('aaBlue')} !important`
      }
    }
  },
  popupIndicator : {
    '&:hover, &:active' : {
      backgroundColor : 'transparent'
    },
    '& .MuiTouchRipple-root' : {
      display : 'none'
    }
  },
  clearIndicator : {
    '&:hover, &:active' : {
      backgroundColor : 'transparent'
    },
    '& .MuiTouchRipple-root' : {
      display : 'none'
    },
    '& .MuiSvgIcon-root' : {
      width     : '0.65em',
      height    : '0.65em',
      '&:hover' : {
        color : dlsColor('caRed')
      }
    }
  }
}));

/**
 * Provides a utility which filters a list of DeviceConfigurations for the
 * currently selected value.
 */
const getSelectedValue = (
  deviceConfigurations: GenericObject[],
  selected: string
): GenericObject => {
  return deviceConfigurations?.find(({name}: GenericObject) => name === selected);
};

/**
 * The DeviceConfigurationChooser component displays a list of deviceConfigurations
 * based on the provided account and appKey, and allows for selecting a notificationType
 * from the list for subsequent use.
 *
 * ```typescript
 *
 *     import {DeviceConfigurationChooser} from '@ion/dls/src/editors/';
 *
 *     // filter the list of deviceConfigurations based on the given asset type and categories ...
 *     <DeviceConfigurationChooser onChange={setRole}
 *       accountId={selectedAccount}
 *       appKey={appKey}
 *       />
 *
 * ```
 *
 * @param    accountId The id of the Account for which deviceConfigurations are to be loaded.
 * @param    selectedValue Selected notificationType value on Editing
 * @param    onChange Callback prop which will be invoked with the selected notificationType.
 * @param    readOnly When true, the component will be displayed in read - only mode;
 *           that is, disabled.Defaults to false.
 * @param    required When true, the required indicator will be displayed.
 * @paramha  hasError When true, the validationError indicator will be displayed.
 * @param    classes Optional classes to apply to the wrapping element.
 * @param    name name for the component that gets passed to the root element.
 */
export const DeviceConfigurationChooser = ({
  accountId,
  selectedValue,
  onChange,
  defaultValue,
  readOnly,
  required,
  classes,
  helperText,
  hasError = false,
  deviceType = {},
  isHardwarePart = false
}: DeviceConfigurationChooserProps) => {
  const _classes                                        = useStyles({hasError});
  const [deviceConfigurations, setDeviceConfigurations] = useState<GenericObject[]>(null);
  const [isLoading, setIsLoading]                       = useState<boolean>(Boolean(accountId));
  const [selected, setSelected]                         = useState<string>(selectedValue?.name || '');
  const [options, setOptions]                           = useState<Options[]>([]);
  const [open, setOpen]                                 = useState(false);
  const hasMounted                                      = useHasMounted();

  useEffect(() => {
    // manages loading deviceConfigurations based on current search term ...
    const loadConfigurations = async function() {
      let results: GenericObject[];
      setIsLoading(true);
      try {
        results = await getDeviceConfigurations(accountId, true);
      } catch (err) {
        error('Device Configurations: Error occurred retrieving Device Configurations');
        error(err);
      } finally {
        if (hasMounted !== false) {
          const deviceConfigurationsList = results?.sort((a, b) => a.name > b.name ? 1 : -1);
          setDeviceConfigurations(deviceConfigurationsList);
          setOptions(getOptions(deviceConfigurationsList));
          setIsLoading(false);
          selected && onChange?.(undefined);
          defaultValue && onChange(getSelectedValue(deviceConfigurationsList, defaultValue));
        }
      }
    };
    loadConfigurations();
  }, []);

  useEffect(() => {
    if (defaultValue && !selected) {
      setSelected(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    if (deviceType && !isObjectEmpty(deviceType)) {
      setOptions(getOptions(filterDeviceConfiguration(deviceType)));
    }
  }, [deviceType]);

  const filterDeviceConfiguration = (deviceTypeObj) => {
    const deviceTypeHref = deviceTypeObj?._self?.href || deviceTypeObj?.href;
    return deviceConfigurations?.filter(configuration => {
      return deviceTypeHref === configuration?.deviceType?.href;
    });
  };

  // invoked when a textfield change is made, delegating the debounce change handler ...
  function handleFieldChange(e: ChangeEvent<HTMLInputElement>) {
    e?.persist();
  };

  return (
    <div className={classes || _classes.root}>
      <FormControl className={_classes.formControl}>
        <Autocomplete disabled={readOnly || isLoading}
          defaultValue={defaultValue || ''}
          autoComplete={false}
          clearOnBlur={false}
          openText=''
          closeText=''
          clearText={translate('common:editors.shared.clearText')}
          classes={{
            popper         : _classes.popper,
            paper          : _classes.paper,
            popupIndicator : _classes.popupIndicator,
            clearIndicator : _classes.clearIndicator,
            endAdornment   : _classes.endAdornment,
            input          : _classes.input,
            inputRoot      : _classes.inputRoot,
            listbox        : _classes.listbox,
            option         : _classes.option
          }}
          open={open}
          onOpen={function() {
            setOpen(true);
          }}
          onClose={function() {
            setOpen(false);
          }}
          getOptionSelected={getOptionSelected}
          getOptionLabel={getOptionLabel}
          onChange={onChange ? function(event, value: Options) {
            const val = value?.label;
            setSelected(val);
            onChange(value ? getSelectedValue(deviceConfigurations, val) : null);
          } : null}
          options={options}
          loading={isLoading}
          selectOnFocus={false}
          clearOnEscape
          loadingText={translate('common:states.loading')}
          noOptionsText={translate('common:editors.shared.noOptionsText')}
          onInputChange={handleFieldChange}
          renderInput={function(params) {
            return (
              <FormTextField {...params}
                autoComplete='off'
                required={required}
                fieldLabel={isHardwarePart ? '' :translate('common:features.deviceConfigurations.label')}
                placeholder={translate('common:features.deviceConfigurations.placeholder')}
                name={translate('common:features.deviceConfigurations.name')}
                error={hasError}
                helperText={helperText || ((hasError && required) ? translate('common:editors.shared.required') : '')}
                InputProps={{
                  ...params.InputProps
                }} />
            );
          }} />
      </FormControl>
    </div>
  );
};

export default DeviceConfigurationChooser;
